import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function PaymentAttachmentPopover({ anchor, anchorId, onClose, children }) {
  const root = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  const lastAnchorRect = useRef(null);
  const [position, setPosition] = useState(null);
  const getAnchor = useCallback(() => document.getElementById(anchorId) || anchor, [
    anchor,
    anchorId,
  ]);
  const update = () => {
    const current = getAnchor();
    const rect = current?.getBoundingClientRect();
    // A parent refresh can temporarily remove the payment row.
    if (current?.isConnected && rect?.width) lastAnchorRect.current = rect;
    else if (current?.isConnected && !rect?.width) {
      close.current();
    }
    const target = lastAnchorRect.current;
    if (!target || !root.current) return;
    const margin = 8;
    const gap = 6;
    const width = Math.min(440, window.innerWidth - 2 * margin);
    if (target.bottom < 0 || target.top > window.innerHeight) close.current();
    const anchorTop = Math.max(margin, Math.min(target.top, window.innerHeight - margin));
    const anchorBottom = Math.max(margin, Math.min(target.bottom, window.innerHeight - margin));
    const below = Math.max(0, window.innerHeight - anchorBottom - margin - gap);
    const above = Math.max(0, anchorTop - margin - gap);
    const showBelow = below >= 360 || below >= above;
    const maxHeight = Math.min(640, showBelow ? below : above);
    const height = Math.min(root.current.getBoundingClientRect().height, maxHeight);
    const next = {
      width,
      left: Math.max(margin, Math.min(target.right - width, window.innerWidth - width - margin)),
      top: showBelow ? anchorBottom + gap : Math.max(margin, anchorTop - gap - height),
      maxHeight,
    };
    setPosition(previous =>
      previous && Object.keys(next).every(key => previous[key] === next[key]) ? previous : next,
    );
  };
  const updateRef = useRef(update);
  updateRef.current = update;
  useLayoutEffect(() => {
    updateRef.current();
  });
  useEffect(() => {
    let frame;
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => updateRef.current());
    };
    const outside = event => {
      if (!root.current?.contains(event.target) && !getAnchor()?.contains(event.target))
        close.current();
    };
    const keyboard = event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close.current();
      }
    };
    const panel = root.current;
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    if (panel) observer?.observe(panel);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', keyboard);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', keyboard);
      if (panel?.contains(document.activeElement)) getAnchor()?.focus();
    };
  }, [getAnchor]);
  return createPortal(
    <div
      ref={root}
      className='payment-attachment-dialog payment-attachment-popover'
      role='dialog'
      aria-modal='false'
      aria-labelledby='payment-attachment-title'
      style={
        position
          ? {
              width: position.width,
              left: position.left,
              top: position.top,
              '--payment-popover-height': `${position.maxHeight}px`,
            }
          : { visibility: 'hidden' }
      }
    >
      <div className='modal-content'>{children}</div>
    </div>,
    document.body,
  );
}
